import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { downloadPdf } from "@/lib/api";
import { money, fmtDate, toISODate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Receipt, Plus, Download, Trash2, Check, Send, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { exportCsv } from "@/lib/csv";

const statusBadge = { draft: "bg-slate-400 text-white", sent: "bg-[color:var(--kpi-warning)] text-white", paid: "bg-[color:var(--kpi-positive)] text-white" };

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [params, setParams] = useSearchParams();

  // wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [wJob, setWJob] = useState("");
  const [wFrom, setWFrom] = useState("");
  const [wTo, setWTo] = useState("");
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState({});
  const [rate, setRate] = useState(0);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, j] = await Promise.all([api.get("/invoices"), api.get("/jobs")]);
      setInvoices(inv.data); setJobs(j.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("new") === "1") { openWizard(); setParams({}, { replace: true }); }
  }, [params]); // eslint-disable-line

  const openWizard = () => { setStep(1); setWJob(""); setWFrom(""); setWTo(""); setEntries([]); setSelected({}); setWizardOpen(true); };

  const fetchEntries = async () => {
    if (!wJob) return toast.error("Select a job");
    let url = `/invoices/uninvoiced?job_id=${wJob}`;
    if (wFrom) url += `&date_from=${wFrom}`;
    if (wTo) url += `&date_to=${wTo}`;
    const res = await api.get(url);
    setEntries(res.data.entries);
    setRate(res.data.hourly_rate);
    const sel = {}; res.data.entries.forEach((e) => (sel[e.id] = true));
    setSelected(sel);
    if (res.data.entries.length === 0) return toast.error("No uninvoiced entries for this selection");
    setStep(2);
  };

  const selEntries = entries.filter((e) => selected[e.id]);
  const subtotal = selEntries.reduce((s, e) => s + e.amount, 0);
  const totalHours = selEntries.reduce((s, e) => s + e.hours, 0);

  const create = async () => {
    if (selEntries.length === 0) return toast.error("Select at least one entry");
    setCreating(true);
    try {
      const res = await api.post("/invoices", { job_id: wJob, entry_ids: selEntries.map((e) => e.id) });
      toast.success(`Invoice ${res.data.invoice_number} created`);
      setWizardOpen(false);
      await load();
      await downloadPdf(`/invoices/${res.data.id}/pdf`, `${res.data.invoice_number}.pdf`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create invoice"); }
    finally { setCreating(false); }
  };

  const setStatus = async (inv, status) => {
    try { await api.put(`/invoices/${inv.id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not update"); }
  };
  const del = async (inv) => {
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Invoice deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };
  const dl = async (inv) => { try { await downloadPdf(`/invoices/${inv.id}/pdf`, `${inv.invoice_number}.pdf`); } catch { toast.error("Download failed"); } };

  const filtered = invoices.filter((i) => filter === "all" || i.status === filter);
  const wizJob = jobs.find((j) => j.id === wJob);

  const exportInvoices = () => {
    if (invoices.length === 0) return toast.error("No invoices to export");
    exportCsv("invoices.csv", [
      { label: "Invoice #", accessor: "invoice_number" },
      { label: "Client", accessor: "client_name" },
      { label: "Job", accessor: "job_title" },
      { label: "Invoice Date", accessor: "invoice_date" },
      { label: "Due Date", accessor: "due_date" },
      { label: "Status", accessor: "status" },
      { label: "Total", accessor: (r) => r.total_amount.toFixed(2) },
    ], invoices);
    toast.success("Invoices exported");
  };

  return (
    <div>
      <PageHeader title="Invoices" subtitle="Create professional PDF invoices from your logged hours."
        actions={<div className="flex gap-2">
          <Button variant="outline" data-testid="invoices-export-button" onClick={exportInvoices}><Download className="h-4 w-4 mr-2" />Export CSV</Button>
          <Button data-testid="invoices-create-button" onClick={openWizard}><Plus className="h-4 w-4 mr-2" />New invoice</Button>
        </div>} />

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList data-testid="invoices-filter-tabs">
          <TabsTrigger value="all" data-testid="invoices-filter-all">All</TabsTrigger>
          <TabsTrigger value="draft" data-testid="invoices-filter-draft">Draft</TabsTrigger>
          <TabsTrigger value="sent" data-testid="invoices-filter-sent">Sent</TabsTrigger>
          <TabsTrigger value="paid" data-testid="invoices-filter-paid">Paid</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? <Card className="p-8 text-center text-muted-foreground">Loading...</Card>
      : filtered.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices here" description="Generate an invoice from uninvoiced timesheet entries."
          action={<Button data-testid="invoices-empty-create-button" onClick={openWizard}><Plus className="h-4 w-4 mr-2" />New invoice</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <Table data-testid="invoices-table">
            <TableHeader><TableRow>
              <TableHead>Invoice</TableHead><TableHead className="hidden md:table-cell">Client / Job</TableHead>
              <TableHead className="hidden sm:table-cell">Due</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((inv) => (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`} className="hover:bg-accent/50">
                  <TableCell><span className="font-mono font-medium">{inv.invoice_number}</span><div className="text-xs text-muted-foreground">{fmtDate(inv.invoice_date)}</div></TableCell>
                  <TableCell className="hidden md:table-cell"><div className="text-sm">{inv.client_name}</div><div className="text-xs text-muted-foreground">{inv.job_title}</div></TableCell>
                  <TableCell className="hidden sm:table-cell text-sm">{fmtDate(inv.due_date)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{money(inv.total_amount)}</TableCell>
                  <TableCell><Badge className={`capitalize ${statusBadge[inv.status]}`} data-testid={`invoice-status-${inv.id}`}>{inv.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Download PDF" data-testid={`invoice-download-${inv.id}`} onClick={() => dl(inv)}><Download className="h-4 w-4" /></Button>
                      {inv.status === "draft" && <Button variant="ghost" size="icon" className="h-8 w-8" title="Mark sent" data-testid={`invoice-mark-sent-${inv.id}`} onClick={() => setStatus(inv, "sent")}><Send className="h-4 w-4" /></Button>}
                      {inv.status !== "paid" && <Button variant="ghost" size="icon" className="h-8 w-8 text-[color:var(--kpi-positive)]" title="Mark paid" data-testid={`invoice-mark-paid-${inv.id}`} onClick={() => setStatus(inv, "paid")}><Check className="h-4 w-4" /></Button>}
                      {inv.status !== "paid" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`invoice-delete-${inv.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete {inv.invoice_number}?</AlertDialogTitle><AlertDialogDescription>The linked timesheet entries will become uninvoiced again.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={() => del(inv)}>Delete</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Wizard */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-wizard">
          <DialogHeader><DialogTitle>Create invoice</DialogTitle></DialogHeader>

          <div className="flex items-center gap-2 mb-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= s ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{s}</div>
                <span className={`text-xs ${step >= s ? "text-foreground font-medium" : "text-muted-foreground"}`}>{["Select job", "Pick entries", "Preview"][s - 1]}</span>
                {s < 3 && <div className="flex-1 h-px bg-border" />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Job *</Label>
                <Select value={wJob} onValueChange={setWJob}>
                  <SelectTrigger data-testid="invoice-wizard-job-select"><SelectValue placeholder="Select a job" /></SelectTrigger>
                  <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} — {j.client_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>From (optional)</Label><DatePicker value={wFrom} onChange={setWFrom} testid="invoice-wizard-date-from" /></div>
                <div className="space-y-1.5"><Label>To (optional)</Label><DatePicker value={wTo} onChange={setWTo} testid="invoice-wizard-date-to" /></div>
              </div>
              <p className="text-xs text-muted-foreground">Leave dates empty to include all uninvoiced billable hours for this job.</p>
              <DialogFooter><Button data-testid="invoice-wizard-next-button" onClick={fetchEntries} disabled={!wJob}>Next <ArrowRight className="h-4 w-4 ml-2" /></Button></DialogFooter>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3 py-2">
              <div className="text-sm text-muted-foreground">Select the entries to include ({money(rate)}/hr).</div>
              <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                {entries.map((e) => (
                  <label key={e.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-accent/40" data-testid={`invoice-entry-${e.id}`}>
                    <Checkbox checked={!!selected[e.id]} onCheckedChange={(v) => setSelected((p) => ({ ...p, [e.id]: v }))} data-testid={`invoice-entry-checkbox-${e.id}`} />
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium">{fmtDate(e.work_date)}</div><div className="text-xs text-muted-foreground truncate">{e.notes || "Work performed"}</div></div>
                    <div className="text-sm tabular-nums text-right"><div>{e.hours.toFixed(2)} hrs</div><div className="text-xs text-muted-foreground">{money(e.amount)}</div></div>
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between rounded-lg bg-secondary/50 px-3 py-2 text-sm">
                <span>{selEntries.length} selected · {totalHours.toFixed(2)} hrs</span>
                <span className="font-semibold tabular-nums">{money(subtotal)}</span>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" data-testid="invoice-wizard-back-button" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
                <Button data-testid="invoice-wizard-preview-button" onClick={() => setStep(3)} disabled={selEntries.length === 0}>Preview <ArrowRight className="h-4 w-4 ml-2" /></Button>
              </DialogFooter>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 py-2">
              <Card className="p-5 border">
                <div className="flex justify-between items-start mb-4">
                  <div><div className="text-lg font-semibold font-display">{wizJob?.client_name}</div><div className="text-xs text-muted-foreground">{wizJob?.title}</div></div>
                  <div className="text-right"><div className="text-xl font-semibold text-primary font-display">INVOICE</div><div className="text-xs text-muted-foreground">Due in 30 days</div></div>
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {selEntries.map((e) => <TableRow key={e.id}><TableCell className="text-sm">{fmtDate(e.work_date)}<div className="text-xs text-muted-foreground truncate max-w-[220px]">{e.notes}</div></TableCell><TableCell className="text-right tabular-nums">{e.hours.toFixed(2)}</TableCell><TableCell className="text-right tabular-nums">{money(e.amount)}</TableCell></TableRow>)}
                  </TableBody>
                </Table>
                <div className="flex justify-end mt-4 pt-3 border-t"><div className="text-right"><div className="text-xs text-muted-foreground">Total Due</div><div className="text-2xl font-semibold text-primary tabular-nums font-display">{money(subtotal)}</div></div></div>
              </Card>
              <DialogFooter className="gap-2">
                <Button variant="outline" data-testid="invoice-wizard-back2-button" onClick={() => setStep(2)}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
                <Button data-testid="invoice-wizard-generate-pdf-button" onClick={create} disabled={creating}><Download className="h-4 w-4 mr-2" />{creating ? "Generating..." : "Generate & download PDF"}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
